import { useRouter } from 'next/router'
import { compose, dissoc, isEmpty, symmetricDifference, take } from 'ramda'
import { useEffect, useMemo, useRef } from 'react'

import { filterProducts } from './useFilteredProducts.helpers'
import usePrevious from './usePrevious'
import useRouterQueryCollectionState from './useRouterQueryCollectionState'

const isEqual = compose(isEmpty, symmetricDifference)

const useFilteredProducts = ({ products: unfilteredProducts, perPage = 9 }) => {
  const router = useRouter()
  const state = useRouterQueryCollectionState()

  const productIds = useRef([])
  const previousProductIds = usePrevious(productIds.current)

  const filteredProducts = useMemo(() => {
    const products = filterProducts({ products: unfilteredProducts, state })
    productIds.current = products.map(({ _id }) => _id)

    return products
  }, [unfilteredProducts, state])

  const totalPages = useMemo(() => Math.ceil(filteredProducts.length / perPage), [filteredProducts, perPage])

  const products =
    state.page === 'all' ? filteredProducts : take(perPage, filteredProducts.slice((state.page - 1) * perPage))

  // If the products have changed, reset the page query param
  useEffect(() => {
    if (!previousProductIds) return undefined

    if (!isEqual(productIds.current, previousProductIds)) {
      router.replace(
        {
          pathname: router.pathname,
          query: dissoc('page', router.query),
        },
        undefined,
        { shallow: true }
      )
    }
  }, [router, productIds, previousProductIds])

  return { state, products, totalPages }
}

export default useFilteredProducts
